import {
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import { createAppRouter } from "@/routes/createAppRouter";
import { PageviewTracker } from "@/lib/analytics/PageviewTracker";


import { LoginScreen } from "@/features/onboarding/LoginScreen";
import { ResetPasswordScreen } from "@/features/onboarding/ResetPasswordScreen";
import { SignupScreen } from "@/features/onboarding/SignupScreen";
import { OnboardingShell } from "@/features/onboarding/OnboardingShell";
import { WelcomeScreen } from "@/features/onboarding/WelcomeScreen";
import { IntakeWizard } from "@/features/onboarding/IntakeWizard";
import { AppIntakeRoute } from "@/features/onboarding/ProductIntake";
import { ExpectScreen } from "@/features/onboarding/ExpectScreen";
import { CompleteScreen } from "@/features/onboarding/CompleteScreen";
import { AppLayout } from "@/components/app-shell";
import { AdminLayout } from "@/components/admin-shell";
import { ClinicianLayout } from "@/components/clinician-shell";
import { InboxScreen } from "@/features/clinician/InboxScreen";
import { ConversationScreen } from "@/features/clinician/ConversationScreen";
import { WorklistScreen } from "@/features/clinician/WorklistScreen";
import { NurseAppointmentsScreen } from "@/features/clinician/NurseAppointmentsScreen";
import { OverviewScreen } from "@/features/clinician/OverviewScreen";
import { TemplatesScreen } from "@/features/clinician/TemplatesScreen";
import { IntakeFormsScreen } from "@/features/clinician/IntakeFormsScreen";
import { GuidesScreen } from "@/features/clinician/GuidesScreen";
import { GuideShareScreen } from "@/features/clinician/GuideShareScreen";
import { ClinicalTeamScreen } from "@/features/clinician/ClinicalTeamScreen";
import { PatientDirectoryScreen } from "@/features/clinician/PatientDirectoryScreen";
import { ReportingScreen } from "@/features/clinician/ReportingScreen";
import { CosignScreen } from "@/features/clinician/CosignScreen";
import { AvailabilityScreen } from "@/features/clinician/AvailabilityScreen";
import { RulesAutomationsScreen } from "@/features/clinician/RulesAutomationsScreen";
import { HomeScreen } from "@/features/home/HomeScreen";
import { AdminHomeScreen } from "@/features/admin/AdminHomeScreen";
import { AdminsScreen } from "@/features/admin/AdminsScreen";
import { PatientProfileBuilderScreen } from "@/features/admin/PatientProfileBuilderScreen";
import { AdminReferralsScreen } from "@/features/admin/AdminReferralsScreen";
import {
  LifecycleCampaignDetailScreen,
  LifecycleEmailsScreen,
  LifecycleEnrollmentsScreen,
} from "@/features/admin/LifecycleEmailsScreen";
import { LifecycleBuilderScreen } from "@/features/admin/LifecycleBuilderScreen";
import { AdminReferralCodeScreen } from "@/features/admin/AdminReferralCodeScreen";
import { ClinicalOpsDetailScreen } from "@/features/admin/ClinicalOpsDetailScreen";
import { ClinicalOpsScreen } from "@/features/admin/ClinicalOpsScreen";
import { ClinicalAdminScreen } from "@/features/admin/clinical-admin/ClinicalAdminScreen";
import { ClinicalAdminReportingScreen } from "@/features/admin/clinical-admin/ClinicalAdminReportingScreen";
import { ClinicalCustomerScreen } from "@/features/admin/clinical-admin/ClinicalCustomerScreen";
import { CohortRetentionScreen } from "@/features/admin/CohortRetentionScreen";
import { CustomerDetailScreen } from "@/features/admin/CustomerDetailScreen";
import { AwaitingOrdersScreen } from "@/features/admin/AwaitingOrdersScreen";
import { SubscriptionFillsScreen } from "@/features/admin/SubscriptionFillsScreen";
import { PharmacyWarningsScreen } from "@/features/admin/PharmacyWarningsScreen";
import { ProductDetailScreen } from "@/features/admin/ProductDetailScreen";
import { SubscriptionsScreen } from "@/features/admin/SubscriptionsScreen";
import { FeatureFlagsScreen } from "@/features/admin/FeatureFlagsScreen";
import { AIOperationsMovedScreen } from "@/features/admin/AIOperationsMovedScreen";
import { SonnyChatScreen } from "@/features/admin/SonnyChatScreen";
import { SONNY_CHAT_ENABLED } from "@/features/admin/sonnyConfig";
import { SystemOpsScreen } from "@/features/admin/SystemOpsScreen";
import { ProtocolsScreen } from "@/features/protocols/ProtocolsScreen";
import { OrdersScreen } from "@/features/orders/OrdersScreen";
import { OrderDetail } from "@/features/orders/OrderDetail";
import { ClinicScreen } from "@/features/clinic/ClinicScreen";
import { ThreadDetail } from "@/features/clinic/ThreadDetail";
import { LearnScreen } from "@/features/learn/LearnScreen";
import { GuideDetail } from "@/features/learn/GuideDetail";
import { ArticleDetail } from "@/features/learn/ArticleDetail";
import { MarketplaceScreen } from "@/features/marketplace/MarketplaceScreen";
import { TreatmentOfferScreen } from "@/features/treatment-offers/TreatmentOfferScreen";
import { SearchResultsScreen } from "@/features/search/SearchResultsScreen";
import { AccountScreen } from "@/features/account/AccountScreen";
import { UpdatePaymentScreen } from "@/features/billing/UpdatePaymentScreen";
import { ReferralProgramScreen } from "@/features/referrals/ReferralProgramScreen";
import { MembershipJoinScreen } from "@/features/membership/MembershipJoinScreen";
import { SupportBoardScreen } from "@/features/admin/support/SupportBoardScreen";
import { SupportMetricsScreen } from "@/features/admin/support/SupportMetricsScreen";
import { SupportViewsScreen } from "@/features/admin/support/SupportViewsScreen";
import { SupportSettingsScreen } from "@/features/admin/support/SupportSettingsScreen";
import {
  RequireAdmin,
  RequireClinical,
  RequireAuth,
  RequireFullAdmin,
  RequireNotOnboarded,
  RequireOnboarded,
} from "@/routes/guards";
import { RootRedirect } from "@/routes/RootRedirect";
import { PreviewIndex } from "@/routes/PreviewIndex";
import { PlanSwitchOfferPreview } from "@/features/account/PlanSwitchOfferModal";
import { LabResultsMockScreen } from "@/features/lab-results/LabResultsMockScreen";
import { LabOrdersScreen, LabOrderScreen } from "@/features/labs/LabOrdersScreen";
import { LabCheckoutScreen } from "@/features/labs/LabCheckoutScreen";
import { LabClaimScreen } from "@/features/labs/LabClaimScreen";
import { LabsGate } from "@/features/labs/labExperiment";

// Dev-only auth-bypass routes so designers can iterate on screens locally
// without signing in. Stripped from production bundles via `import.meta.env.DEV`.
const previewRoutes = import.meta.env.DEV
  ? [
      { path: "/preview", element: <PreviewIndex /> },
      { path: "/preview/welcome", element: <WelcomeScreen /> },
      { path: "/preview/intake", element: <IntakeWizard /> },
      { path: "/preview/expect", element: <ExpectScreen /> },
      { path: "/preview/complete", element: <CompleteScreen /> },
      // Quarterly plan-switch offer modal (renewal nudge) with a mock offer.
      { path: "/preview/plan-offer", element: <PlanSwitchOfferPreview /> },
      {
        path: "/preview",
        element: <AppLayout />,
        children: [
          { path: "home", element: <HomeScreen /> },
          { path: "protocols", element: <ProtocolsScreen /> },
          { path: "clinic", element: <ClinicScreen /> },
          { path: "orders", element: <OrdersScreen /> },
          { path: "learn", element: <LearnScreen /> },
          { path: "marketplace", element: <MarketplaceScreen /> },
          { path: "account", element: <AccountScreen /> },
        ],
      },
    ]
  : [];

// Synthetic integration concepts are visible on staging-backed feature
// previews only. Production builds omit the route entirely.
const stagingPreviewRoutes =
  import.meta.env.VITE_APP_ENV === "staging"
    ? [{ path: "/preview/lab-results", element: <LabResultsMockScreen /> }]
    : [];

/**
 * Pathless layout above every route so `$pageview` fires exactly once per
 * navigation, app-wide. Before this, PageviewTracker lived inside
 * AppLayout + OnboardingShell only — the claim landing (`/`), /login, and
 * the OnboardingShell welcome branch (which early-returns without shell
 * chrome) never emitted pageviews, leaving /onboarding/welcome invisible
 * in PostHog.
 */
// Lifecycle emails deep-linked /app/referral (singular) before the route
// existed as /app/referrals — sent emails hold those links forever, so the
// redirect keeps them working. Search params carry campaign attribution
// (?campaign=...) used by PostHog $current_url filters; preserve them.
function ReferralRedirect() {
  const location = useLocation();
  return <Navigate to={`/app/referrals${location.search}`} replace />;
}

function AnalyticsRoot() {
  return (
    <>
      <PageviewTracker />
      <Outlet />
    </>
  );
}

const routes = [
  { path: "/", element: <RootRedirect /> },
  { path: "/login", element: <LoginScreen /> },
  // Recovery-email landing (login "Forgot password?"). The link's token
  // is exchanged client-side for a session, so this stays OUTSIDE
  // RequireAuth/RequireOnboarded — the screen gates itself and only
  // existing accounts can ever receive the email (no signup surface).
  { path: "/reset-password", element: <ResetPasswordScreen /> },
  // Direct-URL signup for admins/influencers — intentionally not linked
  // from /login or anywhere else in the app. Distribution by URL only.
  { path: "/signup", element: <SignupScreen /> },
  // Public no-login payment-update page for dunning emails/SMS. Auth is the
  // signed `?t=` token (scoped to one customer + past-due invoice) — no
  // Supabase session, so this must stay OUTSIDE RequireAuth.
  { path: "/update-payment", element: <UpdatePaymentScreen /> },
  ...previewRoutes,
  ...stagingPreviewRoutes,
  ...[
    { path: "/labs/claim", element: <LabClaimScreen /> },
    { path: "/app/labs", element: <RequireAuth><AppLayout /></RequireAuth>, children: [
      { index: true, element: <LabsGate><LabOrdersScreen /></LabsGate> },
      { path: "buy/:panelCode", element: <LabCheckoutScreen /> },
      { path: ":orderId", element: <LabOrderScreen /> },
    ] },
  ],
  {
    path: "/onboarding",
    element: (
      <RequireAuth>
        <RequireNotOnboarded>
          <OnboardingShell />
        </RequireNotOnboarded>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/onboarding/welcome" replace /> },
      { path: "welcome", element: <WelcomeScreen /> },
      { path: "intake", element: <IntakeWizard /> },
      { path: "expect", element: <ExpectScreen /> },
      { path: "complete", element: <CompleteScreen /> },
    ],
  },
  { path: "/guides/:token", element: <GuideShareScreen /> },
  {
    path: "/app",
    element: (
      <RequireAuth>
        <RequireOnboarded>
          <AppLayout />
        </RequireOnboarded>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomeScreen /> },
      // Post-onboarding recovery for patients who bailed mid-intake or
      // who purchased a second product after finishing initial onboarding.
      // AppIntakeRoute branches: `?product=` → pre-charge renewal intake
      // (ProductIntake, resolved from the product id with no awaiting row);
      // otherwise the awaiting-row-driven IntakeWizard, which detects the
      // /app/* prefix and exits to /app on submit.
      { path: "intake", element: <AppIntakeRoute /> },
      { path: "protocols", element: <ProtocolsScreen /> },
      { path: "clinic", element: <ClinicScreen /> },
      { path: "clinic/:threadId", element: <ThreadDetail /> },
      {
        path: "appointments",
        element: <Navigate to="/app/clinic?tab=appointments" replace />,
      },
      {
        path: "documents",
        element: <Navigate to="/app/clinic?tab=documents" replace />,
      },
      { path: "orders", element: <OrdersScreen /> },
      { path: "orders/:orderId", element: <OrderDetail /> },
      {
        path: "learn",
        // Keep the library mounted beneath the guide through opening/closing.
        // Articles remain full pages rather than overlays on the library.
        element: (
          <>
            <LearnScreen />
            <Outlet />
          </>
        ),
        children: [{ path: ":slug", element: <GuideDetail /> }],
      },
      { path: "learn/article/:slug", element: <ArticleDetail /> },
      { path: "shop", element: <MarketplaceScreen /> },
      { path: "treatment-offers/:offerId", element: <TreatmentOfferScreen /> },
      { path: "upgrade", element: <Navigate to="/app/shop" replace /> },
      { path: "marketplace", element: <Navigate to="/app/shop" replace /> },
      { path: "search", element: <SearchResultsScreen /> },
      { path: "account", element: <AccountScreen /> },
      { path: "referrals", element: <ReferralProgramScreen /> },
      { path: "membership", element: <MembershipJoinScreen /> },
      { path: "referral", element: <ReferralRedirect /> },
    ],
  },
  {
    path: "/admin",
    element: (
      <RequireAuth>
        <RequireAdmin>
          <AdminLayout />
        </RequireAdmin>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <AdminHomeScreen /> },
      { path: "cohorts", element: <CohortRetentionScreen /> },
      { path: "clinical-ops", element: <ClinicalOpsScreen /> },
      { path: "clinical-ops/:view", element: <ClinicalOpsDetailScreen /> },
      { path: "clinical-admin", element: <ClinicalAdminScreen /> },
      {
        path: "clinical-admin/reporting",
        element: <ClinicalAdminReportingScreen />,
      },
      {
        path: "clinical-admin/customers/:patientId",
        element: <ClinicalCustomerScreen />,
      },
      { path: "support", element: <SupportBoardScreen /> },
      { path: "support/metrics", element: <SupportMetricsScreen /> },
      { path: "support/views", element: <SupportViewsScreen /> },
      { path: "support/settings", element: <SupportSettingsScreen /> },
      { path: "pharmacy/warnings", element: <PharmacyWarningsScreen /> },
      { path: "pharmacy/awaiting", element: <AwaitingOrdersScreen /> },
      { path: "pharmacy/fills", element: <SubscriptionFillsScreen /> },
      { path: "subscriptions", element: <SubscriptionsScreen /> },
      { path: "customers/:customerId", element: <CustomerDetailScreen /> },
      { path: "products/:label", element: <ProductDetailScreen /> },
      { path: "referrals", element: <AdminReferralsScreen /> },
      { path: "lifecycle-emails", element: <LifecycleEmailsScreen /> },
      { path: "lifecycle-emails/builder", element: <LifecycleBuilderScreen /> },
      {
        path: "lifecycle-emails/:campaignKey",
        element: <LifecycleCampaignDetailScreen />,
      },
      {
        path: "lifecycle-emails/:campaignKey/enrollments",
        element: <LifecycleEnrollmentsScreen />,
      },
      { path: "referrals/:code", element: <AdminReferralCodeScreen /> },
      { path: "feature-flags", element: <FeatureFlagsScreen /> },
      {
        path: "sonny",
        element: SONNY_CHAT_ENABLED ? (
          <RequireFullAdmin>
            <SonnyChatScreen />
          </RequireFullAdmin>
        ) : (
          <Navigate to="/admin/ai-operations" replace />
        ),
      },
      {
        path: "ai-operations/*",
        element: <AIOperationsMovedScreen />,
      },
      {
        path: "system-ops",
        element: (
          <RequireFullAdmin>
            <SystemOpsScreen />
          </RequireFullAdmin>
        ),
      },
      { path: "admins", element: <AdminsScreen /> },
      { path: "patient-profiles", element: <PatientProfileBuilderScreen /> },
    ],
  },
  {
    // Clinician portal — a sibling of /admin, not a child. Notably NOT
    // behind RequireOnboarded: a clinician's patient row has no completed
    // intake, so the onboarding gate would bounce them into the wizard.
    path: "/clinician-portal",
    element: (
      <RequireAuth>
        <RequireClinical>
          <ClinicianLayout />
        </RequireClinical>
      </RequireAuth>
    ),
    children: [
      { index: true, element: <InboxScreen /> },
      { path: "overview", element: <OverviewScreen /> },
      { path: "today/:prescriptionId?", element: <WorklistScreen /> },
      { path: "nurse-appointments", element: <NurseAppointmentsScreen /> },
      { path: "availability", element: <AvailabilityScreen /> },
      { path: "inbox/:conversationId", element: <ConversationScreen /> },
      { path: "patients", element: <PatientDirectoryScreen /> },
      { path: "patients/:patientId", element: <PatientDirectoryScreen /> },
      { path: "reports/:reportType?", element: <ReportingScreen /> },
      { path: "cosign/:reviewId?", element: <CosignScreen /> },
      { path: "rules", element: <RulesAutomationsScreen /> },
      {
        path: "templates",
        element: <Navigate to="/clinician-portal/templates/scripts" replace />,
      },
      { path: "templates/scripts", element: <TemplatesScreen /> },
      { path: "templates/intakes", element: <IntakeFormsScreen /> },
      { path: "templates/guides", element: <GuidesScreen /> },
      { path: "team", element: <ClinicalTeamScreen /> },
    ],
  },
  { path: "*", element: <Navigate to="/app" replace /> },
];

export const router = createAppRouter([
  { element: <AnalyticsRoot />, children: routes },
]);
